import React, { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { api } from "../../../lib/api";
import StaffAccessPanel from "../../../components/StaffAccessPanel";
import Field from "../components/Field";

/**
 * Staff tab — CRUD on `db.staff` (the operator roster) plus per-staff
 * StaffAccessPanel for managing access invites/revokes.
 *
 * Extracted from BusinessDashboardShell.jsx (iter28 refactor step 2).
 * No logic, UX or API change — only the file location.
 *
 * `isOwner` is still passed through (back-compat with the StaffAccessPanel
 * `canManage` prop). The panel itself already gates on `staff:manage` server-
 * side; the prop is purely a UI affordance to hide unreachable buttons.
 */
export default function Staff({ businessId, isOwner = true }) {
  const [items, setItems] = useState([]);
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({ name: "", role_title: "", color: "#2563EB", active: true, service_durations: {} });
  const [editing, setEditing] = useState(null);

  async function load() {
    const [s, b] = await Promise.all([
      api.get(`/businesses/${businessId}/staff`),
      api.get(`/businesses/${businessId}`),
    ]);
    setItems(s.data);
    setServices(b.data?.services || []);
  }
  useEffect(() => { load(); }, [businessId]);

  async function submit(e) {
    e.preventDefault();
    // Strip empty/zero overrides so backend doesn't store noise
    const cleaned = Object.fromEntries(Object.entries(form.service_durations || {}).filter(([, v]) => v && Number(v) > 0).map(([k, v]) => [k, Number(v)]));
    const payload = { ...form, service_durations: cleaned };
    if (editing) {
      await api.put(`/staff/${editing}`, payload);
      setEditing(null);
    } else {
      await api.post(`/businesses/${businessId}/staff`, payload);
    }
    setForm({ name: "", role_title: "", color: "#2563EB", active: true, service_durations: {} });
    load();
  }

  async function del(id) {
    if (!window.confirm("Eliminare questo membro dello staff?")) return;
    await api.delete(`/staff/${id}`);
    load();
  }

  const colors = ["#2563EB", "#EC4899", "#10B981", "#F59E0B", "#8B5CF6", "#EF4444", "#06B6D4"];

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <h3 className="font-display text-xl font-medium mb-3">{editing ? "Modifica operatore" : "Nuovo operatore"}</h3>
        <form onSubmit={submit} className="card-soft p-5 space-y-3" data-testid="staff-form">
          <Field label="Nome"><input required className="input-soft" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name" /></Field>
          <Field label="Ruolo"><input className="input-soft" value={form.role_title} onChange={(e) => setForm({ ...form, role_title: e.target.value })} placeholder="Es. Estetista, Trainer, Parrucchiere" data-testid="staff-role" /></Field>
          <Field label="Colore (per agenda)">
            <div className="flex gap-2">
              {colors.map((c) => (
                <button key={c} type="button" onClick={() => setForm({ ...form, color: c })}
                  className={`h-10 w-10 rounded-full border-2 transition ${form.color === c ? "border-zinc-900 scale-110" : "border-transparent"}`}
                  style={{ backgroundColor: c }} data-testid={`staff-color-${c}`} />
              ))}
            </div>
          </Field>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 accent-blue-600" data-testid="staff-active" />
            Attivo (prenotabile)
          </label>

          {services.length > 0 && (
            <div className="pt-3 border-t border-zinc-100 dark:border-zinc-800">
              <div className="text-[10px] font-semibold tracking-[0.15em] uppercase text-zinc-500 mb-1.5 dark:text-zinc-400">Durate personalizzate per servizio</div>
              <p className="text-xs text-zinc-500 mb-2.5 dark:text-zinc-400">Lascia vuoto per usare la durata standard. Imposta minuti diversi se questo operatore è più veloce o lento.</p>
              <div className="space-y-2">
                {services.map((s) => (
                  <div key={s.id} className="flex items-center gap-2" data-testid={`staff-svc-${s.id}`}>
                    <div className="flex-1 text-sm truncate">{s.name} <span className="text-zinc-400 dark:text-zinc-500">({s.duration_min}min)</span></div>
                    <input
                      type="number" min="5" max="480" placeholder={String(s.duration_min)}
                      value={form.service_durations?.[s.id] ?? ""}
                      onChange={(e) => setForm({ ...form, service_durations: { ...form.service_durations, [s.id]: e.target.value } })}
                      className="w-20 h-9 rounded-lg border border-zinc-300 px-2 text-sm focus:border-blue-500 focus:outline-none dark:border-zinc-600"
                      data-testid={`staff-duration-${s.id}`}
                    />
                    <span className="text-xs text-zinc-500 whitespace-nowrap dark:text-zinc-400">min</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button className="btn-accent w-full" data-testid="staff-submit">{editing ? "Aggiorna" : (<><Plus className="h-4 w-4 mr-2" />Aggiungi</>)}</button>
          {editing && <button type="button" onClick={() => { setEditing(null); setForm({ name: "", role_title: "", color: "#2563EB", active: true, service_durations: {} }); }} className="text-sm text-zinc-500 w-full dark:text-zinc-400">Annulla</button>}
        </form>
      </div>
      <div>
        <h3 className="font-display text-xl font-medium mb-3">Team ({items.length})</h3>
        <div className="space-y-3">
          {items.map((s) => (
            <div key={s.id} className="card-soft p-5" data-testid={`staff-row-${s.id}`}>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-2xl flex items-center justify-center text-white font-display text-lg" style={{ backgroundColor: s.color || "#2563EB" }}>
                  {s.name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{s.name}</div>
                  <div className="text-xs text-zinc-500 dark:text-zinc-400">{s.role_title || "—"}{!s.active && " · Non attivo"}</div>
                </div>
                <button onClick={() => { setEditing(s.id); setForm({ name: s.name, role_title: s.role_title || "", color: s.color || "#2563EB", active: s.active, service_durations: s.service_durations || {} }); }} className="text-xs text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100" data-testid={`staff-edit-${s.id}`}>Modifica</button>
                <button onClick={() => del(s.id)} className="h-9 w-9 rounded-full hover:bg-red-50 hover:text-red-600 flex items-center justify-center text-zinc-400 dark:text-zinc-500 dark:bg-red-950/40" data-testid={`staff-del-${s.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <StaffAccessPanel staff={s} businessId={businessId} onChanged={load} canManage={isOwner} />
            </div>
          ))}
          {items.length === 0 && <div className="text-sm text-zinc-500 dark:text-zinc-400">Nessun operatore. Aggiungi il primo membro del team.</div>}
        </div>
      </div>
    </div>
  );
}
